import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../lib/api";
import SitePreview from "../dashboard/SitePreview";

const APP_HOST = new URL(process.env.REACT_APP_BACKEND_URL).hostname;
export const isCustomDomain = () => !["localhost", "127.0.0.1", APP_HOST].includes(window.location.hostname);

function vid(remember) {
  if (!remember) return null;
  try {
    let v = localStorage.getItem("site_vid");
    if (!v) { v = crypto.randomUUID().replace(/-/g, ""); localStorage.setItem("site_vid", v); }
    return v;
  } catch { return null; }
}

const SENT = new Set();

function useTracking(data) {
  useEffect(() => {
    if (!data) return;
    const v = vid(data.remember_visitors);
    const send = (body) => fetch(`${API}/sites/${data.slug}/hit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...body, vid: v }), keepalive: true }).catch(() => {});
    if (!SENT.has(data.slug)) { SENT.add(data.slug); send({ kind: "view", ref: document.referrer && !document.referrer.includes(window.location.host) ? document.referrer : null }); }
    const seen = SENT, queue = new Set();
    const flush = () => { if (queue.size) { send({ kind: "sections", sections: [...queue] }); queue.clear(); } };
    const io = new IntersectionObserver((es) => es.forEach((e) => { const id = e.target.id.slice(4); const key = `${data.slug}:${id}`; if (e.isIntersecting && !seen.has(key)) { seen.add(key); queue.add(id); } }), { threshold: 0.4 });
    const t = setTimeout(() => document.querySelectorAll("[id^='sec-']").forEach((el) => io.observe(el)), 300);
    const iv = setInterval(flush, 4000);
    const hide = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", hide);
    return () => { clearTimeout(t); clearInterval(iv); flush(); io.disconnect(); document.removeEventListener("visibilitychange", hide); };
  }, [data]);
}

function Notice() {
  const [ok, setOk] = useState(() => { try { return localStorage.getItem("site_notice_ok") === "1"; } catch { return true; } });
  if (ok) return null;
  return (
    <div className="fixed bottom-3 inset-x-3 sm:left-auto sm:max-w-sm z-50 bg-white text-slate-800 text-xs shadow-xl rounded-lg p-3 border" data-testid="site-storage-notice">
      This site stores an anonymous ID in your browser to count returning visits. No personal data is collected.
      <button className="ml-2 underline font-semibold" onClick={() => { try { localStorage.setItem("site_notice_ok", "1"); } catch { /* ignore */ } setOk(true); }} data-testid="site-notice-ok">OK</button>
    </div>);
}

export default function PublicSite({ host }) {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const dev = () => (window.innerWidth < 768 ? "mobile" : window.innerWidth < 1100 ? "tablet" : "desktop");
  const [device, setDevice] = useState(dev);
  useEffect(() => {
    axios.get(host ? `${API}/sites/by-domain/${host}` : `${API}/sites/${slug}`).then((r) => { setData(r.data); document.title = r.data.spec.content.site_name; }).catch(() => setMissing(true));
  }, [slug, host]);
  useEffect(() => {
    const on = () => setDevice(dev());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  useTracking(data);
  if (missing) return <div className="min-h-screen flex items-center justify-center p-8 text-center" data-testid="public-site-missing"><div><h1 className="text-3xl">This website isn't available</h1><p className="mt-2 text-slate-500 text-sm">It may have been taken offline by its owner.</p></div></div>;
  if (!data) return <div className="min-h-screen" data-testid="public-site-loading" />;
  return <div data-testid="public-site"><SitePreview spec={data.spec} device={device} live site={{ slug: data.slug, features: data.features || {} }} />{data.remember_visitors && <Notice />}</div>;
}
